/**
 * useBoardData — data layer for the Board page.
 *
 * Two view modes:
 *   - "intakes":  joins `publicIntakes` + `intakeWorkflows` and groups cards
 *                 by `intakeWorkflows.currentStage`. Defaults to the first
 *                 stage (`schedule_meeting`) when no workflow exists yet.
 *   - "projects": reads `projects` and groups by the semantic group
 *                 ("Workflow" | "On Hold" | "Closed") that owns the project's
 *                 `status` value.
 *
 * `moveItem(id, targetColumnId)` performs an optimistic Firestore update.
 * For intakes we patch `intakeWorkflows/{id}.currentStage` and stamp the
 * source stage as completed when the move is forward. For projects we patch
 * `projects/{id}.status` to the first option of the new column group.
 *
 * Optimistic local state reverts on Firestore error so the UI never lies to
 * the user about persisted state.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { updateProject } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  INTAKE_COLUMNS,
  PROJECT_COLUMNS,
  PROJECT_STATUS_TO_COLUMN,
  STAGE_ORDER,
} from "./columns";
import type {
  IntakeWorkflow,
  WorkflowStage,
  WorkflowStageId,
} from "../intakeDetails/types";

export type BoardView = "intakes" | "projects";

export interface IntakeBoardItem {
  id: string;
  kind: "intake";
  title: string;
  agency?: string;
  /** Column id == WorkflowStageId for intakes. */
  columnId: WorkflowStageId;
  status?: string;
  submittedAt?: string;
}

export interface ProjectBoardItem {
  id: string;
  kind: "project";
  title: string;
  /** Column id == status group ("Workflow" | "On Hold" | "Closed"). */
  columnId: string;
  status: string;
  source?: string;
  endDate?: string;
}

export type BoardItem = IntakeBoardItem | ProjectBoardItem;

export interface BoardColumn {
  id: string;
  label: string;
}

interface UseBoardDataReturn {
  items: BoardItem[];
  columns: BoardColumn[];
  loading: boolean;
  /** Move card to a new column. Returns the promise so callers can await it. */
  moveItem: (id: string, targetColumnId: string) => Promise<void>;
}

interface RawIntake {
  id: string;
  intakeName?: string;
  submitterAgency?: string;
  status?: string;
  submittedAt?: string;
  createdAt?: string;
}

interface RawProject {
  id: string;
  name?: string;
  status?: string;
  source?: string;
  endDate?: string;
}

export function useBoardData(view: BoardView): UseBoardDataReturn {
  const { user } = useAuth();
  const [intakes, setIntakes] = useState<RawIntake[]>([]);
  const [workflows, setWorkflows] = useState<IntakeWorkflow[]>([]);
  const [projects, setProjects] = useState<RawProject[]>([]);
  const [loading, setLoading] = useState(true);
  /** Optimistic overrides keyed by item id -> columnId. Cleared on snapshot. */
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  // Subscribe to intakes + workflows when view === "intakes".
  useEffect(() => {
    if (view !== "intakes") return;
    setLoading(true);
    let intakesReady = false;
    let workflowsReady = false;
    const markReady = () => {
      if (intakesReady && workflowsReady) setLoading(false);
    };
    const unsubIntakes = onSnapshot(collection(db, "publicIntakes"), (snap) => {
      const items = snap.docs.map((d) => ({
        ...(d.data() as RawIntake),
        id: d.id,
      }));
      setIntakes(items);
      // Drop optimistic overrides for items that arrived back from Firestore.
      setOverrides((prev) => {
        const next = { ...prev };
        for (const item of items) delete next[item.id];
        return next;
      });
      intakesReady = true;
      markReady();
    });
    const unsubWorkflows = onSnapshot(
      collection(db, "intakeWorkflows"),
      (snap) => {
        const items = snap.docs.map((d) => ({
          ...(d.data() as IntakeWorkflow),
          id: d.id,
        }));
        setWorkflows(items);
        workflowsReady = true;
        markReady();
      }
    );
    return () => {
      unsubIntakes();
      unsubWorkflows();
    };
  }, [view]);

  // Subscribe to projects when view === "projects".
  useEffect(() => {
    if (view !== "projects") return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, "projects"), (snap) => {
      const items = snap.docs.map((d) => ({
        ...(d.data() as RawProject),
        id: d.id,
      }));
      setProjects(items);
      setOverrides((prev) => {
        const next = { ...prev };
        for (const item of items) delete next[item.id];
        return next;
      });
      setLoading(false);
    });
    return () => unsub();
  }, [view]);

  const columns: BoardColumn[] = useMemo(
    () =>
      view === "intakes"
        ? INTAKE_COLUMNS.map((c) => ({ id: c.id, label: c.label }))
        : PROJECT_COLUMNS.map((c) => ({ id: c.id, label: c.label })),
    [view]
  );

  const items: BoardItem[] = useMemo(() => {
    if (view === "intakes") {
      // Join publicIntakes <- intakeWorkflows by id (workflow.id === intakeId per schema).
      const workflowById: Record<string, IntakeWorkflow> = {};
      for (const wf of workflows) {
        const key = wf.intakeId || wf.id;
        if (key) workflowById[key] = wf;
      }
      return intakes.map<IntakeBoardItem>((intake) => {
        const wf = workflowById[intake.id];
        const baseStage: WorkflowStageId = wf?.currentStage ?? "schedule_meeting";
        const override = overrides[intake.id] as WorkflowStageId | undefined;
        return {
          id: intake.id,
          kind: "intake",
          title: intake.intakeName || intake.id,
          agency: intake.submitterAgency,
          columnId: override ?? baseStage,
          status: intake.status,
          submittedAt: intake.submittedAt || intake.createdAt,
        };
      });
    }
    return projects.map<ProjectBoardItem>((project) => {
      const status = project.status || "Submitted";
      const baseColumn = PROJECT_STATUS_TO_COLUMN[status] ?? PROJECT_COLUMNS[0].id;
      const override = overrides[project.id];
      return {
        id: project.id,
        kind: "project",
        title: project.name || project.id,
        columnId: override ?? baseColumn,
        status,
        source: project.source,
        endDate: project.endDate,
      };
    });
  }, [view, intakes, workflows, projects, overrides]);

  const moveItem = useCallback(
    async (id: string, targetColumnId: string) => {
      // Capture pre-move state so we can revert on failure.
      const item = items.find((i) => i.id === id);
      if (!item || item.columnId === targetColumnId) return;
      const previousColumnId = item.columnId;

      // Optimistic update.
      setOverrides((prev) => ({ ...prev, [id]: targetColumnId }));

      try {
        if (view === "intakes") {
          const targetStage = targetColumnId as WorkflowStageId;
          const fromIdx = STAGE_ORDER.indexOf(previousColumnId as WorkflowStageId);
          const toIdx = STAGE_ORDER.indexOf(targetStage);
          const movingForward = fromIdx >= 0 && toIdx > fromIdx;

          const wf = workflows.find(
            (w) => (w.intakeId || w.id) === id
          );
          const stages: WorkflowStage[] = (wf?.stages ?? STAGE_ORDER.map((sid) => ({
            id: sid,
            name: sid,
            status: "pending" as const,
          }))).map((s) => ({ ...s }));

          if (movingForward && fromIdx >= 0 && stages[fromIdx]) {
            stages[fromIdx] = {
              ...stages[fromIdx],
              status: "completed",
              completedAt: new Date().toISOString(),
              completedBy: user?.uid ?? stages[fromIdx].completedBy,
            };
          }
          // Mark the new stage in-progress (or completed if it's the terminal one).
          if (toIdx >= 0 && stages[toIdx]) {
            stages[toIdx] = {
              ...stages[toIdx],
              status:
                targetStage === "completed" ? "completed" : "in-progress",
            };
          }

          await updateDoc(doc(db, "intakeWorkflows", id), {
            currentStage: targetStage,
            stages,
            updatedAt: new Date().toISOString(),
          });
        } else {
          // Projects: pick the first status in the target column group as the
          // canonical landing status. Users can fine-tune later in the modal.
          const target = PROJECT_COLUMNS.find((c) => c.id === targetColumnId);
          if (!target) throw new Error(`Unknown project column: ${targetColumnId}`);
          const newStatus = target.statuses[0];
          // Route through the audited backend handler (not a client-side
          // updateDoc) so the move records the [Auto-generated] status comment +
          // status_transition audit entry the daily briefing reads (OAIP-140).
          await updateProject(id, { status: newStatus });
        }
      } catch (err) {
        // Revert optimistic update.
        setOverrides((prev) => {
          const next = { ...prev };
          // Restore previous column (or drop the override entirely if the
          // previous column matches the underlying snapshot).
          next[id] = previousColumnId;
          return next;
        });
        // eslint-disable-next-line no-console
        console.error("Board moveItem failed:", err);
        throw err;
      }
    },
    [items, view, workflows, user?.uid]
  );

  return { items, columns, loading, moveItem };
}
